import type { Appearance } from '@london-life/contracts';
import { SKINS, type Character } from './game';

// the 12 skin tones of the creator, from very light to very deep; the avatar kit uses the same values as
// material colours (docs AVATARS.md)
export const SKIN_TONE_COLOURS = [
  '#f6d7c3',
  '#efc6a8',
  '#e5b48f',
  '#d9a07a',
  '#c88b65',
  '#b57752',
  '#a06442',
  '#8a5236',
  '#74432c',
  '#5f3623',
  '#4b2a1c',
  '#3a2016',
];

// stand-in colours for each option until the avatar kit has the real parts, so every choice shows at once
const OUTFIT_COLOURS: Record<string, string> = {
  casual: '#3055a0',
  smart_casual: '#6b4f3a',
  office: '#202b38',
  hi_vis: '#d9d22a',
  scrubs: '#2f8f8a',
  tracksuit_mask: '#202b38',
  student_hoodie: '#8255a8',
  agbada: '#e5bd40',
  kaftan: '#cdb894',
  ankara_dress: '#d84937',
  kente: '#ed8840',
  sari: '#c2185b',
  salwar_kameez: '#2e7d32',
  abaya: '#232323',
  thobe: '#efeae0',
  sunday_best: '#5a3e8a',
};
const HAIR_COLOURS: Record<string, string> = {
  black: '#1b1716',
  dark_brown: '#3b2a22',
  brown: '#5a3d2b',
  auburn: '#7a3424',
  blonde: '#c9a35b',
  grey: '#9a9a9a',
};
// today's models have four hair shapes: short, curls, bob and bun; each style uses the nearest
const HAIR_SHAPES: Record<string, number> = {
  short_crop: 0,
  buzz_cut: 0,
  waves: 0,
  high_top: 1,
  cornrows: 0,
  afro: 1,
  twist_out: 1,
  curly_bob: 1,
  long_straight: 2,
  wavy_bob: 2,
  box_braids: 3,
  locs: 3,
  bun: 3,
  ponytail: 3,
  hijab: 3,
  headwrap: 3,
};
const BUILD_SCALES = { slim: 0.92, average: 1, broad: 1.1 } as const;

// Until the modular avatar kit arrives, the preview shows the nearest of today's three models: the feminine
// and in-between frames use the slimmer model, the masculine frame the broader one, the tracksuit-and-mask
// outfit picks the Mandem, and the skin tone the closest colour.
export function toPreviewCharacter(appearance: Appearance, firstName: string): Character {
  const tone = SKIN_TONE_COLOURS[appearance.skinTone - 1] ?? SKIN_TONE_COLOURS[0];
  const shape = HAIR_SHAPES[appearance.hair] ?? 0;
  const brightness = (hex: string) => parseInt(hex.slice(1, 3), 16) + parseInt(hex.slice(3, 5), 16);
  let skin = 0;
  for (let index = 1; index < SKINS.length; index += 1)
    if (Math.abs(brightness(SKINS[index]) - brightness(tone)) < Math.abs(brightness(SKINS[skin]) - brightness(tone)))
      skin = index;
  const feminine = appearance.frame !== 'masculine';
  return {
    name: firstName,
    skin,
    outfit: 1,
    hair: shape,
    skinColour: tone,
    outfitColour: OUTFIT_COLOURS[appearance.outfit] ?? OUTFIT_COLOURS.casual,
    hairColour: HAIR_COLOURS[appearance.hairColour] ?? HAIR_COLOURS.black,
    buildScale: BUILD_SCALES[appearance.build],
    body: feminine ? 'female' : 'male',
    look: !feminine && appearance.outfit === 'tracksuit_mask' ? 'mandem' : 'casual',
    origin: 'fresh',
    trait: 'Social',
  };
}

// another player's look (from People) as a character to draw in 3D
export function lookToCharacter(
  look: { skinTone: number; outfit: string; hair: string; hairColour: string; frame?: string; build?: string },
  firstName: string,
): Character {
  return toPreviewCharacter(
    {
      frame: (['feminine', 'androgynous', 'masculine'].includes(look.frame ?? '')
        ? look.frame
        : 'masculine') as Appearance['frame'],
      build: (['slim', 'average', 'broad'].includes(look.build ?? '') ? look.build : 'average') as Appearance['build'],
      skinTone: look.skinTone,
      hair: look.hair,
      hairColour: look.hairColour,
      facialHair: null,
      outfit: look.outfit,
      accessories: [],
    },
    firstName,
  );
}

// the colours of a look, for a small picture of another player (the People tab, the player card)
export function lookColours(look: { skinTone: number; outfit: string; hairColour: string }) {
  return {
    skin: SKIN_TONE_COLOURS[look.skinTone - 1] ?? SKIN_TONE_COLOURS[5],
    outfit: OUTFIT_COLOURS[look.outfit] ?? OUTFIT_COLOURS.casual,
    hair: HAIR_COLOURS[look.hairColour] ?? HAIR_COLOURS.black,
  };
}

// someone who is always in a place (staff, a visitor): a London face picked from a number, so the same place
// always shows the same people; `outfit` is a uniform colour (hi-vis, scrubs, a bar's black)
const CROWD_OUTFITS = [
  '#3055a0',
  '#6b4f3a',
  '#202b38',
  '#8255a8',
  '#d84937',
  '#2e7d32',
  '#c2185b',
  '#cdb894',
  '#ed8840',
];
export function crowdCharacter(seed: number, outfit?: string): Character {
  let state = (seed * 2654435761) >>> 0;
  const next = (n: number) => {
    state = (state * 1103515245 + 12345) >>> 0;
    return (state >>> 8) % n;
  };
  const feminine = next(2) === 0;
  const look = {
    skinTone: 1 + next(12),
    outfit: 'casual',
    hair: feminine
      ? ['long_straight', 'box_braids', 'curly_bob', 'bun', 'afro', 'wavy_bob'][next(6)]!
      : ['short_crop', 'buzz_cut', 'high_top', 'waves', 'afro'][next(5)]!,
    hairColour: ['black', 'black', 'dark_brown', 'brown', 'blonde', 'auburn', 'grey'][next(7)]!,
    frame: feminine ? 'feminine' : 'masculine',
    build: ['slim', 'average', 'average', 'broad'][next(4)]!,
  };
  const character = lookToCharacter(look, 'Londoner');
  character.outfitColour = outfit ?? CROWD_OUTFITS[next(CROWD_OUTFITS.length)]!;
  return character;
}

// a place's staff look the same every visit (and in the admin's designer): the n-th of a place's staff
export function staffCharacter(placeId: string, index: number, outfit?: string): Character {
  const seed = [...placeId].reduce((sum, letter) => sum * 31 + letter.charCodeAt(0), 7) >>> 0;
  return crowdCharacter(seed + index * 97, outfit);
}
